// src/services/workOrderService.js
// Service module for Work Order related API calls

// Import shared API helpers
import { authenticatedFetch } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// PERFORMANCE UPDATE: In-Memory Mutation-Aware Cache
// ─────────────────────────────────────────────────────────────────────────────
const workOrderCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Clears the local cache. 
 * Triggered automatically on any POST/PUT/DELETE mutation to ensure the 
 * next GET request fetches fresh data from the database.
 */
const clearWorkOrderCache = () => {
  console.log('[WorkOrderService] Cache cleared due to database mutation.');
  workOrderCache.clear();
};

/**
 * Checks if a valid, unexpired cache entry exists for a given key.
 */
const getCachedData = (key) => {
  if (workOrderCache.has(key)) {
    const { data, timestamp } = workOrderCache.get(key);
    if (Date.now() - timestamp < CACHE_TTL) {
      console.log(`[WorkOrderService] ⚡ Cache HIT for key: ${key}`);
      return data;
    }
    // Expired
    workOrderCache.delete(key);
  }
  return null;
};

/**
 * Saves a response to the cache with a timestamp.
 */
const setCachedData = (key, data) => {
  workOrderCache.set(key, { data, timestamp: Date.now() });
};
// ─────────────────────────────────────────────────────────────────────────────


/**
 * Fetches work orders from the backend.
 * Uses the backend's 'fetchAll' bypass to retrieve all records in a single optimized request.
 * @param {object} [filters={}] - Filters object.
 * @returns {Promise<{data: Array, pagination: object}>}
 */
export const fetchWorkOrders = async (filters = {}) => {
  const { forceRefresh, ...cacheFilters } = filters;
  const cacheKey = 'list_' + JSON.stringify(cacheFilters);

  if (!forceRefresh) {
    const cached = getCachedData(cacheKey);
    if (cached) return cached;
  }

  const queryParams = new URLSearchParams();
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.priority) queryParams.append('priority', filters.priority);
  if (filters.assignedTo) queryParams.append('assignedTo', filters.assignedTo);
  if (filters.page) queryParams.append('page', filters.page);
  if (filters.assetId) queryParams.append('asset_id', filters.assetId);
  if (filters.asset_type) queryParams.append('asset_type', filters.asset_type);
  if (filters.source) queryParams.append('source', filters.source);
  if (filters.sla_status) queryParams.append('sla_status', filters.sla_status);
  // --- UPDATE: server-side sort + search (sorting now spans every page, not just the visible one)
  if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
  if (filters.sort_dir) queryParams.append('sort_dir', String(filters.sort_dir).toLowerCase());
  if (filters.search && String(filters.search).trim()) queryParams.append('search', String(filters.search).trim().slice(0, 200));
  // type / domain / linked-record filters
  ['work_order_type', 'domain', 'road_id', 'sensor_id', 'well_id', 'parent_work_order_id', 'inspection_source'].forEach((k) => {
    if (filters[k] !== undefined && filters[k] !== null && filters[k] !== '' && filters[k] !== 'all') queryParams.append(k, filters[k]);
  });
  // --- END UPDATE

  // 🚀 ENTERPRISE UPDATE: Backend Heavy-Lifting
  // Bypassing client-side loop. If fetchAll is true, the backend returns all records 
  // without the 100-record limit cap.
  if (filters.fetchAll) {
      queryParams.append('fetchAll', 'true');
  } else if (filters.limit) {
      queryParams.append('limit', filters.limit);
  } else {
      queryParams.append('limit', '10000'); // Default high limit to prevent implicit capping
  }

  const endpoint = `/workorders${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  console.log(`[WorkOrderService] Fetching work orders from: ${endpoint}`);

  const result = await authenticatedFetch(endpoint);

  // Validate the structure
  if (result && Array.isArray(result.data)) {
    // Ensure pagination object exists even if we bypassed it
    const response = {
        data: result.data,
        pagination: result.pagination || { totalItems: result.data.length }
    };
    setCachedData(cacheKey, response);
    return response; 
  } else {
    console.error("[WorkOrderService] fetchWorkOrders received unexpected data structure:", result);
    throw new Error("Received invalid data format for work orders from server.");
  }
};

/**
 * Creates a new work order.
 * @param {object} workOrderData - Data for the new work order.
 * @returns {Promise<object>} The newly created work order object from the backend response.
 * @throws {Error} If API call fails or auth token is missing.
 */
export const createWorkOrder = async (workOrderData) => {
  const endpoint = '/workorders';
  console.log("[WorkOrderService] Creating work order");
  const options = {
    method: 'POST',
    body: JSON.stringify(workOrderData),
  };
  const result = await authenticatedFetch(endpoint, options);
  
  // PERFORMANCE UPDATE: Invalidate cache because database changed
  clearWorkOrderCache();
  
  return result?.workOrder || result;
};

/**
 * Updates an existing work order.
 * @param {string} id - The ID of the work order to update.
 * @param {object} updateData - Object containing fields to update.
 * @returns {Promise<object>} The updated work order object from the backend response.
 * @throws {Error} If API call fails or auth token is missing.
 */
export const updateWorkOrder = async (id, updateData) => {
  const endpoint = `/workorders/${id}`;
  console.log(`[WorkOrderService] Updating work order ${id}`);
  const options = {
    method: 'PUT',
    body: JSON.stringify(updateData),
  };
  const result = await authenticatedFetch(endpoint, options);

  // PERFORMANCE UPDATE: Invalidate cache because database changed
  clearWorkOrderCache();

  return result?.workOrder || result;
};

/**
 * Deletes a work order by its ID.
 * @param {string} id - The ID of the work order to delete.
 * @returns {Promise<void>} A promise that resolves on successful deletion (backend returns 24 or 200).
 * @throws {Error} If API call fails or auth token is missing.
 */
export const deleteWorkOrder = async (id) => {
  const endpoint = `/workorders/${id}`;
  console.log(`[WorkOrderService] Deleting work order ${id}`);
  const options = { method: 'DELETE' };
  
  await authenticatedFetch(endpoint, options);

  // PERFORMANCE UPDATE: Invalidate cache because database changed
  clearWorkOrderCache();
};

/**
 * Assigns a user to a work order.
 * @param {string} workOrderId - The ID of the work order.
 * @param {string | null} userId - The ID of the user to assign, or null to unassign.
 * @returns {Promise<object>} The updated work order object from the backend response.
 * @throws {Error} If API call fails or auth token is missing.
 */
export const assignUserToWorkOrder = async (workOrderId, userId) => {
  const endpoint = `/workorders/${workOrderId}/assign`;
  console.log(`[WorkOrderService] Assigning user ${userId || 'NULL'} to work order ${workOrderId}`);
  const options = {
    method: 'PUT',
    body: JSON.stringify({ userId: userId || null }),
  };
  const result = await authenticatedFetch(endpoint, options);

  // PERFORMANCE UPDATE: Invalidate cache because database changed
  clearWorkOrderCache();

  return result?.workOrder || result;
};

/**
 * Updates the status of a work order and optionally includes resolution notes.
 * @param {string} workOrderId - The ID of the work order.
 * @param {string} status - The new status.
 * @param {string} [resolution_notes] - Optional notes on resolution.
 * @returns {Promise<object>} The updated work order object.
 */
export const updateWorkOrderStatus = async (workOrderId, status, resolution_notes) => {
    const endpoint = `/workorders/${workOrderId}/status`;
    console.log(`[WorkOrderService] Updating status for work order ${workOrderId} to ${status}`);
    
    const payload = {
        status,
        resolution_notes: resolution_notes || undefined, // Send only if notes exist
    };

    const options = {
        method: 'PUT', 
        body: JSON.stringify(payload),
    };
    
    const result = await authenticatedFetch(endpoint, options);

    // PERFORMANCE UPDATE: Invalidate cache because database changed
    clearWorkOrderCache();

    return result?.workOrder || result;
};


/**
 * Accepts the AI-powered suggestion for a work order assignment.
 * @param {string} workOrderId - The ID of the work order.
 * @returns {Promise<object>} The success message from the backend.
 * @throws {Error} If API call fails.
 */
export const acceptSuggestion = async (workOrderId) => {
    const endpoint = `/workorders/${workOrderId}/accept-suggestion`;
    console.log(`[WorkOrderService] Accepting AI suggestion for work order ${workOrderId}`);
    const options = {
        method: 'POST',
    };
    const result = await authenticatedFetch(endpoint, options);

    // PERFORMANCE UPDATE: Invalidate cache because database changed
    clearWorkOrderCache();

    return result;
};

/**
 * Fetches a single work order by its ID.
 * @param {string} id The ID of the work order.
 * @returns {Promise<object>} The work order object.
 */
export const fetchWorkOrderById = async (id) => {
    const endpoint = `/workorders/${id}`;
    console.log(`[WorkOrderService] Fetching work order by ID: ${id}`);
    
    // We generally don't cache single-item fetches as aggressively, 
    // but if needed in the future, it can be tied into the map here.
    return await authenticatedFetch(endpoint);
};

/**
 * Fetches all work orders associated with a specific asset.
 * @param {string} assetId - The ID of the asset.
 * @returns {Promise<{data: Array, pagination: object}>} Object containing array of work orders.
 */
export const getWorkOrdersForAsset = async (assetId) => {
    if (!assetId) return { data: [], pagination: {} };
    // This utilizes the cached fetchWorkOrders function automatically
    return fetchWorkOrders({ assetId });
};

// --- UPDATE: AI Functions ---

/**
 * Creates a work order directly from an ML prediction payload.
 * Triggers the AI Prediction -> Work Order workflow.
 * @param {object} predictionPayload - The normalized prediction data (asset_id, due_date, context).
 * @returns {Promise<object>} The response containing the created work order.
 */
export const createWorkOrderFromPrediction = async (predictionPayload) => {
    const endpoint = '/work-orders/from-prediction'; 
    console.log("[WorkOrderService] Creating work order from AI prediction");
    const options = {
        method: 'POST',
        body: JSON.stringify(predictionPayload),
    };
    const result = await authenticatedFetch(endpoint, options);

    // PERFORMANCE UPDATE: Invalidate cache because database changed
    clearWorkOrderCache();

    return result;
};

/**
 * Submits feedback for an AI assignment suggestion (Used when user manually overrides or rejects).
 * @param {string} workOrderId - The ID of the work order.
 * @param {boolean} wasCorrect - True if the AI was right, False if the user had to change it.
 * @param {string} [actualTechnicianId] - The ID of the technician the user actually chose.
 * @returns {Promise<object>}
 */
export const submitAssignmentFeedback = async (workOrderId, wasCorrect, actualTechnicianId) => {
    const endpoint = `/workorders/${workOrderId}/feedback`;
    console.log(`[WorkOrderService] Submitting AI feedback for WO ${workOrderId}`);
    const options = {
        method: 'POST',
        body: JSON.stringify({ 
            wasSuggestionCorrect: wasCorrect, 
            actualTechnicianId 
        }),
    };
    return await authenticatedFetch(endpoint, options);
};

/**
 * Fetches global statistics for all work orders in the system
 * @param {boolean} forceRefresh - If true, bypasses the cache.
 * @returns {Promise<object>} The global stats object
 */
export const fetchWorkOrderGlobalStats = async (forceRefresh = false) => {
    const cacheKey = 'global_stats';

    // PERFORMANCE UPDATE: Check cache for global stats
    if (!forceRefresh) {
      const cached = getCachedData(cacheKey);
      if (cached) return cached;
    }

    const endpoint = '/analytics/work-order-stats';
    console.log("[WorkOrderService] Fetching global work order stats");
    const result = await authenticatedFetch(endpoint);

    // Save to cache
    setCachedData(cacheKey, result);
    return result;
};

/**
 * Generates an AI draft email for a citizen regarding a completed work order.
 * @param {string} workOrderId - The ID of the work order.
 * @returns {Promise<string>} The drafted email text.
 */
export const generateCitizenEmailDraft = async (workOrderId) => {
    const endpoint = `/workorders/${workOrderId}/generate-citizen-update`;
    console.log(`[WorkOrderService] Generating AI citizen email draft for WO ${workOrderId}`);
    const options = {
        method: 'POST',
    };
    const result = await authenticatedFetch(endpoint, options);
    return result?.draft_email || result;
};

/**
 * Calls the Ollama-powered backend to generate a technical triage description
 * based on live asset telemetry and AI health scores.
 * @param {object} triageData - { assetName, type, healthScore, currentReading, zone }
 * @returns {Promise<string>} The AI-generated technical description.
 */
export const generateAITriageDescription = async (triageData) => {
    const endpoint = '/ai/asset-triage';
    console.log(`[WorkOrderService] Requesting AI triage for: ${triageData.assetName}`);
    
    const options = {
        method: 'POST',
        body: JSON.stringify(triageData),
    };

    const result = await authenticatedFetch(endpoint, options);
    // Return the description string from the backend response
    return result?.description || `Manual inspection required for ${triageData.assetName}.`;
};

/**
 * Highly reliable fetch specifically for the Live Grid.
 * Fetches all WOs for an asset ID/Name and returns only the single Active one (if any).
 * @param {string} assetIdentifier - The Asset ID or Name.
 * @returns {Promise<object|null>} The active work order object, or null.
 */
export const getLiveGridActiveWorkOrder = async (assetIdentifier) => {
    if (!assetIdentifier) return null;
    
    try {
        const endpoint = `/workorders/asset/${encodeURIComponent(assetIdentifier)}`;
        const result = await authenticatedFetch(endpoint);
        
        const wos = result?.data || [];
        
        // The definitive list of non-terminal statuses
        const activeStatuses = ['pending', 'scheduled', 'open', 'in_progress', 'assigned', 'dispatched'];
        
        const activeWOs = wos.filter(wo => 
            activeStatuses.includes((wo.status || '').toLowerCase())
        );
        
        // Return the most recent active ticket, or null if none exist
        return activeWOs.length > 0 ? activeWOs[0] : null;
    } catch (error) {
        console.error(`[WorkOrderService] Failed to fetch Live Grid active WO for ${assetIdentifier}:`, error);
        return null;
    }
};

/**
 * Triggers the AI Geographic Batch Optimization.
 * @param {number} radiusKm - The search radius for clustering (default 5km).
 * @returns {Promise<object>} The success message.
 */
export const optimizeGeographicRoutes = async (radiusKm = 5) => {
    const endpoint = '/workorders/optimize-routes';
    console.log(`[WorkOrderService] Triggering AI Batch Optimization (Radius: ${radiusKm}km)`);
    
    const options = {
        method: 'POST',
        body: JSON.stringify({ radiusKm }),
    };
    
    const result = await authenticatedFetch(endpoint, options);
    clearWorkOrderCache(); // Invalidate cache so the UI refreshes
    return result;
};

/**
 * --- UPDATE: Sends the (AI-drafted, user-edited) citizen update for a work order.
 * Backend delivers it through the citizen's channel (helpdesk ticket reply / notification
 * orchestrator). Throws with code 'NO_CITIZEN_CHANNEL' when the job has no linked citizen.
 * @param {string} workOrderId
 * @param {string} message - The email/update body.
 * @param {object} [opts]  - { subject }
 * @returns {Promise<object>} { success, channels: [...] }
 */
export const sendCitizenUpdate = async (workOrderId, message, opts = {}) => {
    if (!message || !String(message).trim()) throw new Error('The update is empty.');
    const endpoint = `/workorders/${workOrderId}/send-citizen-update`;
    console.log(`[WorkOrderService] Sending citizen update for WO ${workOrderId}`);
    try {
        const result = await authenticatedFetch(endpoint, {
            method: 'POST',
            body: JSON.stringify({ message: String(message).trim(), subject: opts.subject || undefined }),
        });
        clearWorkOrderCache();
        return result;
    } catch (error) {
        if (/no linked citizen|NO_CITIZEN_CHANNEL/i.test(error?.message || '')) error.code = 'NO_CITIZEN_CHANNEL';
        throw error;
    }
};
// Alias kept for components that call it by its older name
export const sendCitizenEmail = sendCitizenUpdate;
// --- END UPDATE

// --- UPDATE: work order types / domains, AI create helper, inspections ↔ work orders

/**
 * Types, domains (water / sensor / well / road / facility / fleet / internal) and categories.
 * Cached for the session; the UI falls back to its bundled catalog if this fails.
 * @returns {Promise<object>}
 */
export const fetchWorkOrderCatalog = async () => {
    const cached = getCachedData('catalog');
    if (cached) return cached;
    const result = await authenticatedFetch('/workorders/catalog');
    const data = result?.data || result;
    setCachedData('catalog', data);
    return data;
};

/**
 * AI help to create a work order — suggested domain / type / category / priority / title,
 * reasons, crew checklist, step-by-step guide and possible duplicates. Creates nothing.
 * @param {object} draft - whatever the user has typed so far
 * @returns {Promise<object>}
 */
export const getWorkOrderAiDraft = async (draft = {}) => {
    const payload = {};
    ['title', 'description', 'domain', 'work_order_type', 'category', 'priority', 'asset_id', 'road_id', 'sensor_id', 'well_id', 'location_description', 'due_date']
        .forEach((k) => { if (draft[k] !== undefined && draft[k] !== null && draft[k] !== '') payload[k] = draft[k]; });
    return authenticatedFetch('/workorders/ai/draft', { method: 'POST', body: JSON.stringify(payload) });
};

/**
 * Parent + child work orders (inspections and follow-ups) of a work order.
 * @returns {Promise<{supported:boolean,parent:object|null,children:Array,inspections:Array,follow_ups:Array}>}
 */
export const getLinkedWorkOrders = async (workOrderId) => {
    const result = await authenticatedFetch(`/workorders/${workOrderId}/linked`);
    return result?.data || result;
};

/** Create an inspection linked to a work order (pre-work survey or QA after the work). */
export const createLinkedInspection = async (workOrderId, data = {}) => {
    const result = await authenticatedFetch(`/workorders/${workOrderId}/inspections`, { method: 'POST', body: JSON.stringify(data) });
    clearWorkOrderCache();
    return result?.workOrder || result;
};

/** Raise a follow-up work order from an inspection's findings. */
export const createFollowUpWorkOrder = async (workOrderId, data = {}) => {
    const result = await authenticatedFetch(`/workorders/${workOrderId}/follow-ups`, { method: 'POST', body: JSON.stringify(data) });
    clearWorkOrderCache();
    return result?.workOrder || result;
};
// --- END UPDATE

// --- END UPDATE ---

// Combine all functions into a single object and export as default
const workOrderService = {
    fetchWorkOrders,
    fetchWorkOrderById,
    getWorkOrdersForAsset,
    createWorkOrder,
    updateWorkOrder,
    deleteWorkOrder,
    assignUserToWorkOrder,
    updateWorkOrderStatus,
    acceptSuggestion,
    createWorkOrderFromPrediction,
    submitAssignmentFeedback,
    fetchWorkOrderGlobalStats,
    generateCitizenEmailDraft,
    generateAITriageDescription,
    getLiveGridActiveWorkOrder,
    optimizeGeographicRoutes,
    // --- UPDATE
    sendCitizenUpdate,
    sendCitizenEmail,
    fetchWorkOrderCatalog,
    getWorkOrderAiDraft,
    getLinkedWorkOrders,
    createLinkedInspection,
    createFollowUpWorkOrder,
    // --- END UPDATE
};

export default workOrderService;